import type { MouseEvent } from 'react'
import type { FeedbackItem } from '../lib/api.ts'
import { describeFeedback } from '../lib/feedbackItem.ts'
import { toPath } from '../lib/route.ts'
import type { Route } from '../lib/route.ts'
import { checkSides } from '../lib/verificationItems.ts'
import { CHECK_LABEL, label } from '../lib/vocabulary.ts'
import { FOCUS_RING } from './focusRing.ts'
import { TriageControls } from './TriageControls.tsx'

/**
 * One piece of feedback, with what it was about and our classification of it (#405).
 *
 * The finding is read from the snapshot the trail took when the conferência
 * concluded, not from `findings`, which a reverify rewrites -- the same reason
 * the histórico reads it from there.
 */
export function FeedbackCard({
  item,
  highlighted = false,
  onOpen,
  onSave,
  onClear,
  reviewRoute,
}: {
  item: FeedbackItem
  /** Just returned to from its conferência: marked for a moment so the eye finds it. */
  highlighted?: boolean
  /** Plain clicks begin the in-tab review; browser modifier clicks keep their native meaning. */
  onOpen: (item: FeedbackItem, event: MouseEvent<HTMLAnchorElement>) => void
  onSave: (verdict: string, note: string) => Promise<void>
  onClear: () => Promise<void>
  reviewRoute?: Route | undefined
}) {
  const said = describeFeedback(item)
  const check = item.detail?.check
  const sides =
    check === undefined
      ? undefined
      : checkSides({ check, evidence: item.detail?.evidence ?? {} } as never)

  return (
    <li
      data-feedback-id={item.id}
      className={`rounded-base bg-background-raised flex flex-col gap-3 border p-4 transition-colors duration-700 ${
        highlighted ? 'border-stroke-brand bg-fill-brand-weak' : 'border-stroke-weak'
      }`}
    >
      <div className="text-tSmall text-text-weak flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-text-strong font-semibold">{item.account_name}</span>
        <a
          href={toPath(reviewRoute ?? { name: 'feedbackReview', eventId: item.id })}
          className={`text-text-brand rounded-base font-semibold ${FOCUS_RING}`}
          onClick={(event) => onOpen(item, event)}
        >
          Filial {item.branch_code} · Orçamento {item.quotation_number}
        </a>
        <span>{item.actor_email}</span>
        <span className="ml-auto tabular-nums">{new Date(item.at).toLocaleString('pt-BR')}</span>
      </div>

      {check !== undefined && (
        <div className="flex flex-col gap-1">
          <span className="text-tPSemi text-text-strong">
            {label(CHECK_LABEL, check)}
            {item.ruleTitle !== undefined && (
              <span className="text-text-weak font-normal"> · {item.ruleTitle}</span>
            )}
          </span>
          {item.detail?.message !== undefined && (
            <span className="text-tP text-text-weak">{item.detail.message}</span>
          )}
          {sides !== undefined &&
            (sides.prescribed !== undefined || sides.quoted !== undefined) && (
              <span className="text-tSmall text-text-weak">
                Receita: <span className="text-text-strong">{sides.prescribed ?? '—'}</span> ·
                Orçamento: <span className="text-text-strong">{sides.quoted ?? '—'}</span>
              </span>
            )}
        </div>
      )}

      <div className="flex flex-col gap-0.5">
        <span className={`text-tPSemi ${said.contests ? 'text-text-error' : 'text-text-strong'}`}>
          {said.answer}
          {said.reason !== undefined && (
            <span className="text-text-weak font-normal"> · {said.reason}</span>
          )}
        </span>
        {said.note !== undefined && (
          <blockquote className="border-stroke-weak text-tP text-text-strong border-l-2 pl-3">
            {said.note}
          </blockquote>
        )}
      </div>

      <div className="border-stroke-weak border-t pt-3">
        <TriageControls
          // A fresh form per judgement, so a change made elsewhere is not
          // shown under a draft started before it.
          key={`${item.verdict ?? ''}:${item.triaged_at ?? ''}`}
          claim={item.claim}
          verdict={item.verdict}
          note={item.triage_note}
          by={item.triaged_by}
          at={item.triaged_at}
          onSave={onSave}
          onClear={onClear}
        />
      </div>
    </li>
  )
}
